import {defineStore} from 'pinia'
import param from 'jquery-param'
import api from '@/services/api'
import {getTypeOptions} from '@/utils/filter'
import {usePreloadViewItemDataStore} from '@/stores/preloadViewItemData'
import {useComponentsStore} from '@/stores/components'
import {useFieldsetsStore} from '@/stores/fieldsets'
import {useUserStore} from '@/stores/user'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'
import {useAppStore} from '@/stores/appStore'

export function useViewStore(viewId) {
    return defineStore(viewId, {
        state: () => ({
            listingName: undefined,
            
            // The name space is provided via a ".<name>" suffix to the listingName. E.g. view:blog.author
            namespace: undefined,
            
            // Listings which appear in modals are namespaced so that their settings do not interfere with the non-
            // namespaced version. Some features are disabled for namespaced modules.
            isNamespaced: false,
            
            tableName: undefined,
            componentId: undefined,
            
            // Listing service
            selectMode: false,
            showFilters: true,
            allItemsSelected: false,
            selectedIds: [],
            showArchive: false,
            onSelect: undefined,
            
            // Listing settings
            orderBy: '',
            orderDirection: 'Asc',
            pageSize: 20,
            viewMode: 'table',
            columns: [],
            showSearchToolbar: false,
            searchTerm: '',
            page: 1,
            
            filters: [],
            savedFilters: [],
            
            hiddenFilters: {},
            
            ids: [],
            allIds: [],
            
            // The total number of ids across all pages
            // Setting it to zero indicates that the current filters have no results.
            itemCount: undefined,
            
            allSelected: false,
            
            loadTimeout: undefined,
        }),
        getters: {
            selectedIdsLength(state) {
                return state.selectedIds.length
            },
            activeFilters(state) {
                return state.filters.filter(o => o.value !== undefined && o.value !== '')
            },
            isFilterActive(state) {
                return (
                    state.searchTerm
                    || this.activeFilters.length > 0
                )
            },
            visibleColumnIds(state) {
                let data = []
                const userStore = useUserStore()
                const componentStructureStore = useComponentStructureStore()
                
                // The component's fields have duplicate display orders across the different fieldsets. E.g. name => 1,
                // createdDate => 1, so they must be ordered by fieldset.
                let allFields = componentStructureStore.get(state.componentId)
                
                // Remove the administrator and assume columns
                // todo This should be tied to a tableName
                if (!userStore.user?.administrator) {
                    allFields = allFields.filter(o => !['administrator', 'assume'].includes(o.columnName))
                }
                
                const fieldsetsStore = useFieldsetsStore()
                fieldsetsStore.items
                    // The fieldsets are returned ordered by display order, so collect the fields associated to each one.
                    .filter(o => o.componentId === state.componentId)
                    .forEach(o => {
                        const fieldsetId = o.id
                        const fields = allFields.filter(o => o.fieldsetId === fieldsetId && state.columns.includes(o.id))
                        data.push(...fields)
                    })
                
                // Now add the fields which aren't assigned to a fieldset e.g. Display Order
                const fields = allFields.filter(o => o.fieldsetId === 0 && state.columns.includes(o.id))
                data.push(...fields)
                
                return data.map(a => a.id)
            },
            shareUrl(state) {
                let url = window.location.origin + '/#/'
                
                url += state.tableName + '/'
                
                const params = {
                    pageSize: state.pageSize,
                    viewMode: state.viewMode
                }
                
                if (state.page > 1) {
                    params.page = state.page
                }
                
                if (state.orderBy) {
                    params.sort = state.orderDirection === 'Desc' ? '-' : ''
                    params.sort += state.orderBy
                }
                
                if (state.searchTerm) {
                    params.searchTerm = state.searchTerm
                }
                
                if (state.showArchive) {
                    params.showArchive = 1
                }
                
                if (this.activeFilters) {
                    this.activeFilters.forEach(o => {
                        if (o.predicate === '=') {
                            params[o.columnName] = o.value
                        } else {
                            params[o.columnName] = params[o.columnName] || {}
                            params[o.columnName][o.predicate] = o.value
                        }
                    })
                }
                
                if (Object.keys(params).length > 0) {
                    url += '?' + param(params)
                }
                
                return url
            }
        },
        actions: {
            mut_showArchive(showArchive) {
                this.showArchive = showArchive
            },
            mut_toggleShowArchive() {
                this.showArchive = !this.showArchive
            },
            mut_setPageSize(pageSize) {
                this.pageSize = pageSize
            },
            mut_setOrderBy(orderBy) {
                this.orderBy = orderBy
            },
            mut_setOrderDirection(orderDirection) {
                this.orderDirection = orderDirection
            },
            mut_setViewMode(viewMode) {
                this.viewMode = viewMode
            },
            mut_setColumns(columns) {
                this.columns = columns
            },
            mut_setPage(n) {
                this.page = parseInt(n) || 0
            },
            mut_pageDown() {
                this.page--
            },
            mut_pageUp() {
                this.page++
            },
            mut_toggleSearchToolbar() {
                this.showSearchToolbar = !this.showSearchToolbar
            },
            mut_searchTerm(str) {
                this.searchTerm = str
            },
            mut_addFilter(o) {
                this.filters.push(o)
            },
            mut_deleteFilter(key) {
                this.filters.splice(key, 1)
            },
            mut_deleteAllFilters() {
                this.filters.length = 0
            },
            mut_resetFilter(key) {
                this.filters[key].value = undefined
            },
            mut_resetAllFilters() {
                this.searchTerm = ''
                this.page = 1
                
                this.filters.forEach(o => o.value = undefined)
            },
            mut_saveFilters(name) {
                let filter = this.savedFilters.find(o => o.name === name)
                if (filter) {
                    filter.filters = JSON.parse(JSON.stringify(this.filters))
                } else {
                    filter = this.savedFilters.find(o => JSON.stringify(o.filters) === JSON.stringify(this.filters))
                    if (filter) {
                        filter.name = name
                    } else {
                        this.savedFilters.push({
                            name: name,
                            filters: JSON.parse(JSON.stringify(this.filters))
                        })
                    }
                }
            },
            mut_deleteSavedFilter(name) {
                const i = this.savedFilters.map(o => o.name).indexOf(name)
                this.savedFilters.splice(i, 1)
            },
            selectAll() {
                this.selectedIds = []
                if (!this.allSelected) {
                    this.ids.forEach((itemId) => {
                        this.selectedIds.push(itemId)
                    })
                }
                this.allSelected = !this.allSelected
                this.allItemsSelected = false
            },
            selectNone() {
                this.selectedIds = []
                this.allSelected = false
                this.allItemsSelected = false
            },
            selectAllItems() {
                this.allItemsSelected = true
            },
            onShiftClick(id) {
                let len = this.selectedIds.length
                
                if (len) {
                    let lastSelectedId = this.selectedIds[len - 1]
                    let keys = []
                    
                    this.ids.forEach((itemId, key) => {
                        if (itemId === lastSelectedId) {
                            keys.push(key)
                        }
                        if (itemId === id) {
                            keys.push(key)
                        }
                    })
                    
                    let start = keys.shift()
                    let end = keys.pop()
                    let ids = this.ids.slice(start, end + 1)
                    
                    ids.forEach((itemId) => {
                        if (this.selectedIds.indexOf(itemId) === -1) {
                            this.selectedIds.push(itemId)
                        }
                    })
                }
            },
            onMetaClick(id) {
                let i = this.selectedIds.indexOf(id)
                if (i === -1) {
                    this.selectedIds.push(id)
                } else {
                    this.selectedIds.splice(i, 1)
                    this.allSelected = false
                }
            },
            init({
                     listingName,
                     selectedIds,
                     hiddenFilters,
                     selectMode,
                     onSelect,
                     query
                 }) {
                const componentsStore = useComponentsStore()
                const componentStructureStore = useComponentStructureStore()
                
                this.listingName = listingName
                
                this.tableName = this.listingName.split('.')[0]
                this.componentId = componentsStore.componentIds[this.tableName]
                this.namespace = this.listingName.split('.')[1]
                this.isNamespaced = this.listingName.indexOf('.') > -1
                
                const settings = JSON.parse(localStorage.getItem(this.listingName) || '{}')
                
                if (settings.showSearchToolbar) {
                    this.toggleSearchToolbar()
                }
                
                if (settings.filters && Object.keys(this.filters).length === 0) {
                    settings.filters.forEach(o => this.addFilter(o))
                }
                
                this.savedFilters = settings.savedFilters || []
                
                this.selectedIds = selectedIds || []
                this.hiddenFilters = hiddenFilters || {}
                this.selectMode = selectMode || false
                this.onSelect = onSelect || undefined
                
                const allColumns = componentStructureStore.get(this.componentId)
                
                const validIds = new Set(allColumns.map(o => o.id))
                const columnIds = settings.columns?.length
                    ? settings.columns.filter(id => validIds.has(id))
                    : allColumns.filter(o => o.browser).map(o => o.id)
                
                this.setColumns(columnIds)
                
                const primaryKey = 'id'
                if (
                    settings.orderBy
                    && (
                        allColumns.find(o => o.columnName === settings.orderBy)
                        || settings.orderBy === primaryKey
                    )
                    && !query?.sort
                ) {
                    this.setOrderBy(settings.orderBy)
                } else {
                    this.setOrderBy('id')
                    this.setOrderDirection('Desc')
                }
                
                if (settings.orderDirection && !query?.sort) {
                    this.setOrderDirection(settings.orderDirection)
                }
                
                if (settings.pageSize && !query?.pageSize) {
                    this.setPageSize(settings.pageSize)
                }
                
                if (settings.viewMode && !query?.viewMode) {
                    this.setViewMode(settings.viewMode)
                }
                
                if (query && Object.keys(query).length > 0) {
                    this.mut_setPage(query.page || this.page || 0)
                    this.mut_showArchive(!!query.showArchive)
                    
                    if (query.searchTerm) {
                        this.mut_searchTerm(query.searchTerm)
                    }
                    
                    if (query.pageSize) {
                        this.setPageSize(parseInt(query.pageSize))
                        delete query.pageSize
                    }
                    
                    if (query.viewMode) {
                        this.setViewMode(query.viewMode)
                        delete query.viewMode
                    }
                    
                    let sort = query.sort
                    if (sort) {
                        this.setOrderBy(sort?.indexOf('-') === 0 ? sort.substr(1) : sort)
                        this.setOrderDirection(sort?.indexOf('-') === 0 ? 'Desc' : 'Asc')
                        delete query.sort
                    }
                    
                    if (Object.keys(query).length > 0) {
                        let doReset = true
                        const columns = componentStructureStore.get(this.componentId)
                        for (let columnName in query) {
                            if (query.hasOwnProperty(columnName)) {
                                const value = query[columnName]
                                
                                let predicate
                                if (columnName.indexOf('[') > -1) {
                                    const parts = columnName.split(/[\[\]]/)
                                    columnName = parts[0]
                                    predicate = parts[1]
                                } else {
                                    predicate = '='
                                }
                                
                                let type
                                if (columnName === 'id') {
                                    type = 'number'
                                } else {
                                    const column = columns.find(o => o.columnName === columnName)
                                    if (!column) {
                                        console.warn(columnName + ' not found.')
                                        continue
                                    }
                                    type = column.type
                                }
                                
                                if (doReset) {
                                    doReset = false
                                    this.deleteAllFilters()
                                }
                                
                                const typeOptions = getTypeOptions(type)
                                const option = typeOptions.find(o => o.predicate === predicate)
                                this.addFilter({
                                    columnName,
                                    type,
                                    condition: option.condition,
                                    predicate,
                                    conditionText: option.text,
                                    value
                                })
                            }
                        }
                    }
                }
                
                this.scheduleLoad()
            },
            scheduleLoad() {
                this.ids = []
                
                if (this.loadTimeout) {
                    clearTimeout(this.loadTimeout)
                }
                
                let delay = 250
                
                this.loadTimeout = setTimeout(() => {
                    this.load()
                }, delay)
            },
            async load() {
                const componentsStore = useComponentsStore()
                const userStore = useUserStore()
                const componentStructureStore = useComponentStructureStore()
                const appStore = useAppStore()
                
                if (this.tableName) {
                    if (
                        this.tableName === 'product_options'
                        || this.tableName === 'product_variables'
                    ) {
                        appStore.navigationComponent = 'product_types'
                    } else if (this.tableName.indexOf('product_variations__') === 0) {
                        appStore.navigationComponent = 'm_products_products'
                    }
                }
                
                const searchParams = this.hiddenFilters
                
                if (userStore.user?.franchiseId && this.tableName !== 'users') {
                    const hasFranchiseIdColumn = !!componentStructureStore.get(this.componentId)
                        .find(o => o.columnName === 'franchiseId')
                    
                    if (hasFranchiseIdColumn) {
                        searchParams.franchiseId = userStore.user.franchiseId
                        await this.sendLoadViewRequest({searchParams})
                        return
                    }
                    
                    const franchiseObjs = []
                    let level = 0
                    const maxLevel = 1
                    
                    const findFranchiseComponent = async (componentId) => {
                        const tableName = componentsStore.items.find(o => o.id === componentId).tableName
                        const objs = componentStructureStore.get(componentId)
                        
                        const franchiseIdColumn = objs.find(o => o.columnName === 'franchiseId')
                        if (franchiseIdColumn) {
                            await applyFranchiseFilter(tableName, 'franchiseId', userStore.user.franchiseId)
                            return
                        }
                        
                        const item = objs.find(o => o.type === 'relationshipOneToMany' && o.required)
                        if (item) {
                            franchiseObjs.push({
                                tableName,
                                columnName: item.columnName,
                            })
                            
                            if (level <= maxLevel) {
                                level++
                                await findFranchiseComponent(item.categoryComponentId)
                            } else {
                                await this.sendLoadViewRequest({searchParams})
                            }
                        } else {
                            await this.sendLoadViewRequest({searchParams})
                        }
                    }
                    
                    const applyFranchiseFilter = async (tableName, columnName, ids) => {
                        if (franchiseObjs.length === 0) {
                            searchParams[columnName] = ids
                            await this.sendLoadViewRequest({searchParams})
                            return
                        }
                        
                        const o = await api.post(`api/component/${tableName}/id`, {
                            [columnName]: ids,
                        }, {
                            headers: {'X-Http-Method-Override': 'GET'},
                        })
                        
                        const newIds = o.data.values
                        const obj = franchiseObjs.pop()
                        await applyFranchiseFilter(obj.tableName, obj.columnName, newIds)
                    }
                    
                    await findFranchiseComponent(this.componentId)
                    return
                }
                
                await this.sendLoadViewRequest({searchParams})
            },
            async sendLoadViewRequest({searchParams}) {
                const componentsStore = useComponentsStore()
                const componentStructureStore = useComponentStructureStore()
                const itemDataStore = useItemDataStore()
                const postData = {
                    __getAllIds: true
                }
                
                const component = componentsStore.items.find(o => o.tableName === this.tableName)
                
                if (component.useArchive) {
                    postData.isArchived = this.showArchive ? 1 : 0
                }
                
                const start = this.page - 1
                if (start) {
                    postData.start = start * this.pageSize
                }
                
                postData.limit = this.pageSize
                
                if (
                    this.orderBy
                    && (
                        this.orderBy === 'id'
                        || componentStructureStore.get(this.componentId).find(o => o.columnName === this.orderBy)
                    )
                ) {
                    const sortPrefix = this.orderDirection === 'Desc' ? '-' : ''
                    postData.sort = sortPrefix + this.orderBy
                }
                
                if (this.searchTerm) {
                    postData.search = this.searchTerm
                }
                
                if (Object.keys(searchParams).length) {
                    Object.assign(postData, searchParams)
                }
                
                if (this.activeFilters) {
                    this.activeFilters.forEach(o => {
                        if (o.predicate === '=') {
                            postData[o.columnName] = o.value
                        } else {
                            postData[o.columnName] = postData[o.columnName] || {}
                            postData[o.columnName][o.predicate] = o.value
                        }
                    })
                }
                
                const {data: {values: ids, allIds}} = await api.post(`api/component/${this.tableName}/id`, postData, {
                    headers: {'X-Http-Method-Override': 'GET'},
                })
                
                this.allIds = allIds
                
                const objs = await itemDataStore.preload({tableName: this.tableName, ids})
                
                const preloadViewItemDataStore = usePreloadViewItemDataStore()
                await preloadViewItemDataStore.get({
                    tableName: this.tableName,
                    visibleColumns: this.columns,
                    objs
                })
                
                this.ids = ids
                this.itemCount = allIds.length
            },
            async toggleShowArchive() {
                this.mut_toggleShowArchive()
                await this.setLocalStorage()
                await this.setLocation()
                this.scheduleLoad()
            },
            async setPageSize(pageSize) {
                this.mut_setPageSize(pageSize)
                await this.setLocalStorage()
                await this.setLocation()
                this.scheduleLoad()
            },
            async setOrderBy(orderBy) {
                this.mut_setOrderBy(orderBy)
                await this.setLocalStorage()
                await this.setLocation()
                this.scheduleLoad()
            },
            async setOrderDirection(orderDirection) {
                this.mut_setOrderDirection(orderDirection)
                await this.setLocalStorage()
                this.scheduleLoad()
            },
            async setViewMode(viewMode) {
                this.mut_setViewMode(viewMode)
                await this.setLocalStorage()
                this.setLocation()
            },
            setColumns(columns) {
                this.mut_setColumns(columns)
                this.setLocalStorage()
            },
            async setPage(n) {
                this.mut_setPage(n)
                await this.setLocation()
                this.scheduleLoad()
            },
            async pageDown() {
                this.mut_pageDown()
                await this.setLocation()
                this.scheduleLoad()
            },
            async pageUp() {
                this.mut_pageUp()
                await this.setLocation()
                this.scheduleLoad()
            },
            toggleSearchToolbar() {
                this.mut_toggleSearchToolbar()
                this.setLocalStorage()
            },
            async searchTermAction(str) {
                this.mut_searchTerm(str)
                this.mut_setPage(1)
                await this.setLocation()
                this.scheduleLoad()
            },
            addFilter(o) {
                if (!o) {
                    o = {
                        columnName: 'id',
                        type: 'number',
                        condition: 'is',
                        predicate: '=',
                        conditionText: 'is',
                        value: ''
                    }
                }
                
                if (o.value === undefined) o.value = undefined
                
                this.mut_addFilter(o)
                this.setLocalStorage()
            },
            async setFilterProp({key, name, value}) {
                this.filters[key][name] = value
                
                if (name === 'value') {
                    this.page = 1
                }
                
                this.setLocalStorage()
                this.setLocation()
                this.scheduleLoad()
            },
            async deleteFilter(key) {
                this.mut_deleteFilter(key)
                this.setLocalStorage()
                this.setLocation()
                this.scheduleLoad()
            },
            deleteAllFilters() {
                this.mut_deleteAllFilters()
                this.setLocalStorage()
            },
            async resetFilter(key) {
                this.mut_resetFilter(key)
                await this.setLocalStorage()
                await this.setLocation()
                this.scheduleLoad()
            },
            async resetAllFilters() {
                this.mut_resetAllFilters()
                await this.setLocation()
                this.scheduleLoad()
            },
            saveFilters(name) {
                this.mut_saveFilters(name)
                this.setLocalStorage()
            },
            deleteSavedFilter(name) {
                this.mut_deleteSavedFilter(name)
                this.setLocalStorage()
            },
            setLocalStorage() {
                localStorage.setItem(this.listingName, JSON.stringify({
                    pageSize: this.pageSize,
                    orderBy: this.orderBy,
                    orderDirection: this.orderDirection,
                    viewMode: this.viewMode,
                    columns: this.columns,
                    showArchive: this.showArchive,
                    showSearchToolbar: this.showSearchToolbar,
                    filters: this.filters,
                    savedFilters: this.savedFilters
                }))
            },
            setLocation() {
                if (!this.isNamespaced) {
                    window.location.replace(this.shareUrl)
                }
            },
            loadSavedFilter(name) {
                this.filters.splice(0)
                
                const savedFilter = this.savedFilters.find(o => o.name === name)
                savedFilter.filters.forEach(o => this.addFilter(JSON.parse(JSON.stringify(o))))
                
                this.scheduleLoad()
                this.setLocation()
            },
            onClick(id) {
                if (this.onSelect) {
                    this.onMetaClick(id)
                    this.onSelect(this.selectedIds)
                } else {
                    let i = this.selectedIds.indexOf(id)
                    if (
                        i === -1
                        || this.allSelected
                        || this.selectedIds.length > 1
                    ) {
                        this.selectedIds = [id]
                    } else {
                        this.selectedIds = []
                    }
                    
                    this.allSelected = false
                }
            },
            onDblClick() {
                const userStore = useUserStore()
                if (
                    !this.onSelect
                    && !this.selectMode
                    && this.tableName.indexOf('product_variations__') !== 0
                ) {
                    return userStore.access(this.tableName, 'edit')
                }
            }
        }
    })()
}